import type {
  AvailableLanguages,
  CommentEntity,
  NovelBookmarkEntity,
  TitleChapters,
} from "~/src/api";
import { getNovelThemes } from "~/src/novel/themes";
import { NovelSettings } from "~/src/api";
import type { Infer } from "superstruct";

export interface NovelSettingsType extends Infer<typeof NovelSettings> {}

export enum NovelReaderPreset {
  SINGLE,
  DOUBLE,
  LONGSTRIP,
}

const getDefaultSettings = (): Omit<
  NovelSettingsType,
  "listCondition" | "condition" | "profile"
> => ({
  templates: getNovelThemes(),
  templateIndex: 0,
  readingMode: "longstrip",

  textMargin: 0.5,
  // maxWidth: 1536,

  openMenuOnPageLoad: false,
  showCommentIcons: true,
  showBookmarkIcons: false,
  hideReadingHint: false,
});

export const NovelSettingsPresetMap = new Map<
  NovelReaderPreset,
  Omit<NovelSettingsType, "listCondition" | "condition" | "profile">
>();
NovelSettingsPresetMap.set(NovelReaderPreset.SINGLE, {
  ...getDefaultSettings(),
  readingMode: "single",
});
NovelSettingsPresetMap.set(NovelReaderPreset.DOUBLE, {
  ...getDefaultSettings(),
  readingMode: "double",
});
NovelSettingsPresetMap.set(NovelReaderPreset.LONGSTRIP, {
  ...getDefaultSettings(),
  readingMode: "longstrip",
});

const Desktop: NovelSettingsType = {
  profile: "reader.novel.settings.profile.desktop",
  condition: "screen.width > 640",
  listCondition: "",
  ...NovelSettingsPresetMap.get(NovelReaderPreset.SINGLE)!,
};

const Mobile: NovelSettingsType = {
  profile: "reader.novel.settings.profile.mobile",
  condition: "screen.width <= 640",
  listCondition: "",
  ...NovelSettingsPresetMap.get(NovelReaderPreset.SINGLE)!,
};

export const NovelProfileMap = {
  [Desktop.profile]: Desktop,
  [Mobile.profile]: Mobile,
};

export interface NovelSettingsInterface {
  currentProfileIndex: number;
  profiles: NovelSettingsType[];

  menuOpen: boolean;
  secondaryMenuOpen: "comments" | "bookmarks" | "footnotes" | false;
}

export const getDefaultNovelSettings = (): NovelSettingsInterface => {
  return {
    currentProfileIndex: 0,
    profiles: Object.values(NovelProfileMap),

    menuOpen: false,
    secondaryMenuOpen: false,
  };
};

interface NovelContentEvent {
  paragraphId: string;
}

export interface NovelCommentEvent {
  paragraphId: string | undefined;
  comment: CommentEntity;
}

export interface NovelBookmarkEvent extends NovelContentEvent {
  // state indicates what the bookmark was changed to, adding = true, removing = false
  state: boolean;
  bookmark?: NovelBookmarkEntity;
}

export interface NovelHighlightEvent extends NovelBookmarkEvent {
  state: true;

  // start post + length of highlight
  offset: number;
  length: number;
}

export interface NovelReaderEvents {
  (e: "bookmark", v: NovelBookmarkEvent): void;

  (e: "highlight", v: NovelHighlightEvent): void;

  (e: "comment", v: NovelCommentEvent): void;

  (e: "return"): void;
}

export interface NovelReaderMethods {
  // adds a bookmark with bookmark id, this is fine since bookmarks are unversioned
  addBookmark: (paragraphId: string, bookmarkId?: string) => Promise<void>;
  // removes a bookmark and returns its id value, if any
  removeBookmark: (paragraphId: string) => Promise<void>;
  // sets current chapter relative to chapterIdSequence
  setChapterIndex: (index: number) => string | null;
  // sets the reading order of a series of chapters
  setAggregate: (aggregate: TitleChapters) => void;

  // chapter navigation
  switchChapter: (chapterId: string) => void;
  nextChapter: () => void;
  prevChapter: () => void;
  hasNextChapter: ComputedRef<boolean>;
  hasPrevChapter: ComputedRef<boolean>;

  // current chapter id
  chapterId: ComputedRef<string>;

  // TODO: these fns need impls
  // addHighlight: (paragraphId: string, highlightId?: string, start?: number, length?: number) => void;
  // removeHighlight: (highlightId?: string) => void;
}

/**
 * Returns true margin value from input between 0-1'
 * start, middle, end: 16, 32, 256
 * @param value an input from 0-1
 */
export const getTextMargin = (value: number) => {
  if (value < 0.5) return 16 * 4 ** value;
  return 256 * value ** 3;
};

export const scrollElementIntoViewById = (id: string) => {
  document.getElementById(id)?.scrollIntoView({
    block: "center",
  });
};
